import { Breadcrumb } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { Paths } from 'constants/index';
import { useTranslation } from 'react-i18next';

const breadcrumbRedirectMap: Record<string, string> = {
  '/sites': Paths.SITES_ACTIVE,
  '/tools': Paths.TOOLS_ACTIVE,
  '/workers': Paths.WORKERS_ACTIVE,
};

const breadcrumbDisabledPaths = new Set([
  '/admin',
]);

const breadcrumbDisabledPatterns = [
  /^\/sites\/\d+\/projects$/,
];

// Routes whose trailing path segment is a dynamic, non-numeric value (e.g. a scanned
// QR barcode) - render as a generic "Details" label instead of falling through to
// "Not found", matching how numeric :id segments are already handled below.
const breadcrumbDynamicPatterns = [
  /^\/tools\/overview\/[^/]+$/,
];

const Breadcrumbs = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const pathSnippets = location.pathname.split('/').filter((i) => i);

  const extraBreadcrumbItems = pathSnippets.map((value, index) => {
    const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
    const resolvedUrl = breadcrumbRedirectMap[url] ?? url;
    const isNumber = !isNaN(+value);
    const isDynamicSegment = isNumber || breadcrumbDynamicPatterns.some((p) => p.test(url));
    const key = `pages${url.replaceAll('/','.').replaceAll(/[.]\d+/g, '')}`;
    let valueTranslated = t('pages.notfound');

    if (i18n.exists(key)) {
      const translation = isNumber ? t('pages.details') : i18n.t(key, { returnObjects: true });
      valueTranslated = translation['title' as keyof object] || translation ;
    } else if (isDynamicSegment) {
      valueTranslated = t('pages.details');
    }

    const isDisabled = breadcrumbDisabledPaths.has(url) || breadcrumbDisabledPatterns.some((p) => p.test(url));

    return (
      <Breadcrumb.Item key={url}>
        {isDisabled ? <span>{valueTranslated}</span> : <Link to={resolvedUrl}>{valueTranslated}</Link>}
      </Breadcrumb.Item>
    );
  });

  const items = [
    <Breadcrumb.Item key={Paths.HOME}>
      <Link to={Paths.HOME}>{t('pages.home')}</Link>
    </Breadcrumb.Item>,
  ].concat(extraBreadcrumbItems);

  return <Breadcrumb>{items}</Breadcrumb>;
};

export default Breadcrumbs;
